import {
  IconAdd20,
  IconPersonNew20,
  Spinner,
} from '@lightspeed/unified-components-helios-theme/react'
import { tv } from '@lightspeed/unified-components-helios-theme/preset'
import { comboboxThemeConfig } from '@lightspeed/unified-components-helios-theme/theme'
import { BaseCombobox } from '@lightspeed/unified-react-components'
import type {
  ComboboxProps,
  DropdownListRenderProps,
} from '@lightspeed/unified-react-components'
import { useT } from '@transifex/react'
import { CustomerBadge } from '@vend/react-utilities'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { useCustomerSearch } from './useCustomerSearch'

const heliosComboboxStyles = tv(comboboxThemeConfig)
// Keep viewport-fitted dropdowns below the 50px register top bar, with an
// additional 8px gutter so the first result is not flush against it.
const TOP_BAR_SAFE_INSET = 58
const comboboxParts = Object.keys(comboboxThemeConfig.slots) as Array<
  keyof typeof comboboxThemeConfig.slots
>

const getComboboxClasses = (opened: boolean, suggestionsClassName?: string) => {
  const styles = heliosComboboxStyles({ opened })
  const defaultClasses: Record<string, string> = {}

  comboboxParts.forEach(part => {
    defaultClasses[part] = styles[part]()
  })

  return {
    ...defaultClasses,
    container: styles.container({
      class: 'max-h-[inherit] overflow-hidden bg-neutral-top',
    }),
    content: styles.content({ class: 'min-h-0' }),
    footer: styles.footer({ class: 'shrink-0 bg-neutral-top p-0' }),
    positioner: styles.positioner({
      class: ['wr-customer-search-combobox-positioner', suggestionsClassName]
        .filter(Boolean)
        .join(' '),
    }),
    trigger: styles.trigger({ class: 'order-first' }),
  }
}

export interface CustomerSearchProps {
  onSelect: (customer: any) => void
  onAdd: (searchValue?: string) => void
  placeholder: string
  searchValue?: string
  onFetch?: (query: string) => void
  positioning?: ComboboxProps['positioning']
  suggestionsClassName?: string
}

export interface CustomerSearchHandle {
  focusAndSelectInput: () => void
}

export const CustomerSearch = forwardRef<
  CustomerSearchHandle,
  CustomerSearchProps
>((props, ref) => {
  const t = useT()
  const models = useCustomerSearch(props)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasDirectInputChangeRef = useRef(false)
  const trimmedInputValue = models.inputValue.trim()
  const firstCustomerId = models.options[0]?.items[0]?.value ?? null
  const [highlightedCustomerId, setHighlightedCustomerId] = useState<
    string | null
  >(firstCustomerId)

  useEffect(() => {
    setHighlightedCustomerId(firstCustomerId)
  }, [firstCustomerId])

  useImperativeHandle(
    ref,
    () => ({
      focusAndSelectInput: () => {
        const input = containerRef.current?.querySelector('input')
        input?.focus()
        input?.select()
      },
    }),
    []
  )

  const addCustomerCopy = trimmedInputValue
    ? t('Add "{customer}" as a new customer', {
        _context: 'Text for customer step in quote modal.',
        customer: trimmedInputValue,
      })
    : t('Add a new customer', {
        _context: 'Input placeholder text for customer step in quote modal.',
      })

  const handleOpenChange = (opened: boolean) => {
    if (!opened) models.handleOpened(false)
  }

  const handleAddCustomer = () => {
    models.handleOpened(false)
    props.onAdd(trimmedInputValue)
  }

  const handleComboboxInputValueChange = (value: string) => {
    if (!hasDirectInputChangeRef.current) return

    hasDirectInputChangeRef.current = false
    models.handleInputValueChange(value)
  }

  // Keep the combobox ref and use the row element to scroll highlighted
  // customers into view in the scrollable suggestions list.
  const renderCustomer: NonNullable<DropdownListRenderProps['renderItem']> = ({
    item,
    props: itemProps,
    state,
  }) => {
    const customer = models.customersById.get(item.value)

    if (!customer) return null

    return (
      <div
        {...itemProps}
        className={`flex min-h-12 cursor-pointer items-center gap-2 px-4 py-3 ${
          state.highlighted
            ? 'bg-go-highlight-default'
            : 'bg-neutral-top hover:bg-go-highlight-default active:bg-go-highlight-default'
        }`}
        key={item.value}
        ref={element => {
          const itemRef = (itemProps as typeof itemProps & {
            ref?: React.Ref<HTMLDivElement>
          }).ref

          if (typeof itemRef === 'function') itemRef(element)
          else if (itemRef) {
            ;(itemRef as React.MutableRefObject<HTMLDivElement | null>).current = element
          }

          if (element && state.highlighted && element.scrollIntoView) {
            element.scrollIntoView({ block: 'nearest' })
          }
        }}
      >
        <CustomerBadge
          apiCustomer={customer}
          group={customer.customerGroup}
          size="x-small"
        />
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className="uc-base w-full font-general"
      onChangeCapture={event => {
        const target = event.target as HTMLElement

        if (target.matches('[data-part="input"]')) {
          hasDirectInputChangeRef.current = true
        }
      }}
      onKeyDownCapture={event => {
        if (event.key === 'Enter' && !trimmedInputValue) {
          event.preventDefault()
        }
      }}
      onClickCapture={event => {
        const target = event.target as HTMLElement

        if (target.closest('[data-part="input-wrapper"]')) {
          event.stopPropagation()
        }
      }}
      onPointerDownCapture={event => {
        const target = event.target as HTMLElement

        if (target.closest('[data-part="input-wrapper"]')) {
          event.stopPropagation()
        }
      }}
      onFocusCapture={event => {
        const target = event.target as HTMLElement

        if (target.matches('[data-part="input"]')) {
          event.stopPropagation()
        }
      }}
      onClick={event => event.stopPropagation()}
    >
      <BaseCombobox
        customClasses={getComboboxClasses(
          models.isOpen,
          props.suggestionsClassName
        )}
        filterFn={() => true}
        footerSlot={
          <div className="flex min-h-[60px] w-full bg-neutral-top">
            <button
              className="group inline-flex min-h-[60px] w-full min-w-20 cursor-pointer items-center justify-start gap-1 rounded-none border-none bg-transparent px-6 py-2 font-general text-go-default shadow-transparent typography-body-sm-emphasized hover:bg-go-highlight-default active:bg-go-highlight-strong focus-visible:outline focus-visible:outline-xs focus-visible:outline-offset-md focus-visible:outline-border-go-default"
              data-track="add-as-new-customer"
              onMouseDown={event => {
                // The combobox content prevents mousedown from moving focus away
                // from its input, but its footer does not. Keep the input focused
                // so the blur handler cannot close the combobox before this
                // button's click event opens the customer modal.
                event.preventDefault()
              }}
              onClick={handleAddCustomer}
              type="button"
            >
              <IconAdd20 aria-hidden className="size-4" />
              <span className="mx-0 text-[15px] font-bold leading-5">
                {addCustomerCopy}
              </span>
            </button>
          </div>
        }
        inputValue={models.inputValue}
        loading={models.isLoading}
        loadingSlot={
          <Spinner customClasses={{ icon: 'text-go-default' }}>
            {t('Loading…')}
          </Spinner>
        }
        noResultsSlot={t('No results for “{query}”', {
          _context: 'Customer search empty state',
          query: trimmedInputValue,
        })}
        onChange={models.handleChange}
        highlightedValue={highlightedCustomerId}
        onHighlightChange={({
          highlightedValue,
        }: {
          highlightedValue: string | null
        }) => setHighlightedCustomerId(highlightedValue ?? firstCustomerId)}
        onInputValueChange={handleComboboxInputValueChange}
        onOpenChange={handleOpenChange}
        opened={models.isOpen}
        options={models.isLoading ? [] : models.options}
        placeholder={props.placeholder}
        positioning={{
          placement: 'bottom-start',
          flip: true,
          fitViewport: true,
          overflowPadding: TOP_BAR_SAFE_INSET,
          ...props.positioning,
        }}
        renderItem={renderCustomer}
        selectionMode="single"
        triggerSlot={<IconPersonNew20 aria-hidden />}
      />
    </div>
  )
})
